import { useState } from "react";
import {
  Search,
  Filter,
  Phone,
  Mail,
  MoreHorizontal,
  Sparkles,
  Upload,
  ChevronDown,
  Loader2,
  Zap,
  History,
  X,
  Route,
  Globe,
  Link,
  FileSpreadsheet,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useLeads, useScoreLead, useScoreAllLeads } from "@/hooks/useLeads";
import { formatDistanceToNow } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { AddLeadDialog } from "@/components/leads/AddLeadDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AIQuickActions } from "@/components/ai/AIQuickActions";
import { AIScoreBadge } from "@/components/ai/AIScoreBadge";
import { LeadPrioritization } from "@/components/ai/LeadPrioritization";
import { LeadAutoRoutingPanel } from "@/components/leads/LeadAutoRoutingPanel";
import { AIMarketDiscovery } from "@/components/leads/AIMarketDiscovery";
import { AILeadHarvest } from "@/components/leads/AILeadHarvest";
import { BulkLeadImport } from "@/components/leads/BulkLeadImport";
import { LeadDetailSheet } from "@/components/leads/LeadDetailSheet";
import { ActivityTimeline } from "@/components/activities/ActivityTimeline";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const statusConfig = {
  new: { label: "New", className: "bg-blue-500/10 text-blue-400" },
  attempted_contact: { label: "Attempted", className: "bg-yellow-500/10 text-yellow-400" },
  engaged: { label: "Engaged", className: "bg-purple-500/10 text-purple-400" },
  qualified: { label: "Qualified", className: "bg-success/10 text-success" },
  unqualified: { label: "Unqualified", className: "bg-muted text-muted-foreground" },
  converted: { label: "Converted", className: "bg-success/10 text-success" },
};

const sourceLabels: Record<string, string> = {
  manual: "Manual",
  import: "Import",
  web_form: "Web Form",
  api: "API",
  list_purchase: "List Purchase",
  enrichment: "Enrichment",
  referral: "Referral",
};

function ScoreIndicator({ score, label }: { score: number; label: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{score}</span>
      </div>
      <div className="score-indicator w-20">
        <div
          className={cn(
            "score-indicator-fill",
            score >= 80 && "bg-success",
            score >= 60 && score < 80 && "bg-primary",
            score < 60 && "bg-warning"
          )}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

export default function Leads() {
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLeadForActivity, setSelectedLeadForActivity] = useState<{id: string; name: string} | null>(null);
  const [selectedLeadForDetail, setSelectedLeadForDetail] = useState<any>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [showRouting, setShowRouting] = useState(false);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [showHarvest, setShowHarvest] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const { data: leads, isLoading, error } = useLeads();
  const scoreLead = useScoreLead();
  const scoreAllLeads = useScoreAllLeads();

  const handleViewLead = (lead: any) => {
    setSelectedLeadForDetail(lead);
    setDetailSheetOpen(true);
  };

  const filteredLeads = leads?.filter((lead) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(search) ||
      lead.last_name?.toLowerCase().includes(search) ||
      lead.email?.toLowerCase().includes(search) ||
      lead.company_name?.toLowerCase().includes(search)
    );
  });

  const toggleLead = (id: string) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (!filteredLeads) return;
    setSelectedLeads((prev) =>
      prev.length === filteredLeads.length ? [] : filteredLeads.map((l) => l.id)
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">Error loading leads: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leads</h1>
          <p className="text-muted-foreground">
            Manage and nurture your sales leads
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2" onClick={() => setShowDiscovery(!showDiscovery)} data-wizard="ai-discovery">
            <Globe className="h-4 w-4" />
            AI Discovery
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowHarvest(!showHarvest)}>
            <Link className="h-4 w-4" />
            Harvest
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowRouting(!showRouting)}>
            <Route className="h-4 w-4" />
            Auto-Routing
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              if (leads && leads.length > 0) {
                scoreAllLeads.mutate(leads.map(l => l.id));
              }
            }}
            disabled={scoreAllLeads.isPending || !leads || leads.length === 0}
          >
            {scoreAllLeads.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Score All Leads
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowBulkImport(!showBulkImport)}>
            <FileSpreadsheet className="h-4 w-4" />
            Import CSV
          </Button>
          <AddLeadDialog />
        </div>
      </div>

      {/* AI Market Discovery Panel */}
      <Collapsible open={showDiscovery} onOpenChange={setShowDiscovery}>
        <CollapsibleContent className="animate-accordion-down">
          <AIMarketDiscovery />
        </CollapsibleContent>
      </Collapsible>

      {/* AI Lead Harvest Panel */}
      <Collapsible open={showHarvest} onOpenChange={setShowHarvest}>
        <CollapsibleContent className="animate-accordion-down">
          <AILeadHarvest />
        </CollapsibleContent>
      </Collapsible>

      {/* Bulk CSV Import Panel */}
      <Collapsible open={showBulkImport} onOpenChange={setShowBulkImport}>
        <CollapsibleContent className="animate-accordion-down">
          <BulkLeadImport />
        </CollapsibleContent>
      </Collapsible>

      {/* AI Lead Auto-Routing Panel */}
      <Collapsible open={showRouting} onOpenChange={setShowRouting}>
        <CollapsibleContent className="animate-accordion-down">
          <LeadAutoRoutingPanel />
        </CollapsibleContent>
      </Collapsible>

      {/* AI Lead Prioritization */}
      {leads && leads.length > 0 && (
        <div data-wizard="qualification">
          <LeadPrioritization 
            leads={leads} 
            onLeadSelect={(leadId) => {
              const element = document.querySelector(`[data-lead-id="${leadId}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('ring-2', 'ring-primary');
                setTimeout(() => element.classList.remove('ring-2', 'ring-primary'), 2000);
              }
            }}
          />
        </div>
      )}

      {/* Filters Bar */}
      <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search leads..."
              className="pl-10 bg-muted/50 border-border"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filter
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                Status: All
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>All</DropdownMenuItem>
              <DropdownMenuItem>New</DropdownMenuItem>
              <DropdownMenuItem>Engaged</DropdownMenuItem>
              <DropdownMenuItem>Qualified</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="ml-auto flex items-center gap-2">
            {selectedLeads.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => {
                  scoreAllLeads.mutate(selectedLeads);
                  setSelectedLeads([]);
                }}
                disabled={scoreAllLeads.isPending}
              >
                {scoreAllLeads.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
                Score Selected ({selectedLeads.length})
              </Button>
            )}
            <span className="ai-badge">
              <Sparkles className="h-3 w-3" />
              AI Scoring Active
            </span>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="glass rounded-xl overflow-hidden" data-wizard="leads-table">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : !filteredLeads || filteredLeads.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-lg mb-2">No leads found</p>
            <p className="text-sm">Add your first lead to get started with Aiveliq360</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/30">
                    <th className="text-left px-5 py-3">
                      <input
                        type="checkbox"
                        checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                        onChange={toggleAll}
                        className="rounded border-border"
                      />
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Contact
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Source
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Status
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      AI Score
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Conversion
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Tags
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Last Activity
                    </th>
                    <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {filteredLeads.map((lead) => {
                    const status = lead.status || "new";
                    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.new;

                    return (
                      <tr
                        key={lead.id}
                        data-lead-id={lead.id}
                        className={cn(
                          "hover:bg-muted/30 transition-all cursor-pointer",
                          selectedLeads.includes(lead.id) && "bg-muted/20"
                        )}
                        onClick={() => handleViewLead(lead)}
                      >
                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(lead.id)}
                            onChange={() => toggleLead(lead.id)}
                            className="rounded border-border"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <div>
                            <p className="font-medium text-foreground">
                              {lead.first_name} {lead.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {lead.company_name || "No company"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {lead.email}
                            </p>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-muted-foreground">
                            {sourceLabels[lead.source || "manual"] || lead.source}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={cn("stage-pill", config.className)}>
                            {config.label}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <ScoreIndicator score={lead.lead_score || 0} label="Lead Score" />
                        </td>
                        <td className="px-5 py-4">
                          <ScoreIndicator
                            score={lead.ai_conversion_score || 0}
                            label="Conversion"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(lead.tags || []).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-muted-foreground">
                            {lead.last_activity_at
                              ? formatDistanceToNow(new Date(lead.last_activity_at), { addSuffix: true })
                              : "No activity"}
                          </span>
                        </td>
                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <AIQuickActions 
                              entityType="lead" 
                              entity={lead}
                            />
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10"
                                    onClick={() => scoreLead.mutate(lead.id)}
                                    disabled={scoreLead.isPending}
                                  >
                                    {scoreLead.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Sparkles className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Score with AI</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() => handleViewLead(lead)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            >
                              <Phone className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() => setSelectedLeadForActivity({
                                id: lead.id,
                                name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Lead'
                              })}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewLead(lead)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setSelectedLeadForActivity({
                                  id: lead.id,
                                  name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Lead'
                                })}>
                                  <History className="h-4 w-4 mr-2" />
                                  View Activity
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => scoreLead.mutate(lead.id)}>
                                  <Sparkles className="h-4 w-4 mr-2" />
                                  Score with AI
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-5 py-4 border-t border-border/50 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Showing {filteredLeads.length} leads
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled>
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Activity Timeline Sheet */}
      <Sheet open={!!selectedLeadForActivity} onOpenChange={(open) => !open && setSelectedLeadForActivity(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Activity - {selectedLeadForActivity?.name}
            </SheetTitle>
          </SheetHeader>
          {selectedLeadForActivity && (
            <div className="mt-6">
              <ActivityTimeline leadId={selectedLeadForActivity.id} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Lead Detail Sheet */}
      <LeadDetailSheet 
        lead={selectedLeadForDetail}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />
    </div>
  );
}
